import { Container } from '@/components/Container';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { useAffiliate } from '@/hooks/useAffiliate';
import { useTranslation } from '@/hooks/useTranslation';
import { ExternalLink, Star, MapPin, Check, Quote } from 'lucide-react';
import googleThumbs from '../../../public/images/google/thumbs.json';
import { useAnalytics } from '@/hooks/useAnalytics';

/* ═══════════════════════════════════════════
   POUSADA DESTAQUE — Fim da Trilha
   Card full-viewport 50/50 com máximo de info
   ═══════════════════════════════════════════ */

const pousada = {
  handle: 'pousada-fim-da-trilha',
  title: 'Pousada Fim Da Trilha',
  subtitle: 'End Of Trail Inn — hospedagem e gastronomia em Encantadas',
  description:
    'A Pousada Fim Da Trilha é uma das hospedagens mais bem avaliadas da Ilha do Mel, com nota 4.6 no Google baseada em mais de 600 avaliações. Localizada no coração de Encantadas, a poucos passos da praia, oferece quartos com ar-condicionado, café da manhã incluso e atendimento acolhedor. O grande diferencial é o Restaurante Fim da Trilha no mesmo complexo — eleito pelo Guia Quatro Rodas, famoso pela paella de frutos do mar e pelo ambiente rústico no meio da mata. Reserve direto pelo WhatsApp e garanta o melhor preço.',
  provider: {
    name: 'Pousada Fim Da Trilha',
    phone: '4134269017',
    whatsappMessage: 'Olá! Vi a Pousada Fim da Trilha no vemprailhadomel.com.br e quero fazer uma reserva!',
    commission: '10-15%',
    partnerSince: '2026-04',
    directBooking: true,
    bookingComUrl: 'https://www.booking.com/hotel/br/fim-da-trilha',
  },
  vilarejoName: 'Encantadas',
  location: {
    distanceToBeach: '1 min',
    distanceToPier: '3 min',
  },
  amenities: [
    'wi-fi',
    'cafe-da-manha',
    'ar-condicionado',
    'restaurante',
    'estacionamento-pontal',
    'varanda',
    'cozinha-compartilhada',
  ],
  roomTypes: [
    {
      name: 'Quarto Standard',
      description: 'Cama de casal, ar-condicionado, banheiro privativo, café da manhã incluso',
      price: 280,
    },
    {
      name: 'Quarto Família',
      description: 'Cama de casal + beliche, ar-condicionado, banheiro privativo, café da manhã incluso',
      price: 380,
    },
  ],
  priceRange: { min: 280, max: 500, currency: 'BRL', season: 'média' },
  rating: { booking: 9.0, tripAdvisor: 4.6, google: 4.6 },
  reviewCount: { booking: 607, tripAdvisor: 150, google: 607 },
  reviews: [
    {
      source: 'google',
      author: 'Visitante',
      text: 'Pousada incrível! Localização perfeita em Encantadas, café da manhã delicioso e o restaurante Fim da Trilha é simplesmente imperdível.',
      rating: 5,
    },
    {
      source: 'booking',
      author: 'Carlos M.',
      text: 'Excelente custo-benefício. Atendimento familiar, quartos limpos e a paella do restaurante é a melhor da ilha.',
      rating: 9,
    },
  ],
  directBookingAdvantage: [
    'Preço melhor que no Booking',
    'Sem taxa de comissão',
    'Atendimento direto com a pousada',
    'Acesso prioritário ao restaurante Fim da Trilha',
    'Dicas exclusivas do que fazer na ilha',
    'Cancelamento mais fácil',
  ],
};

function amenityLabel(key: string): string {
  const map: Record<string, string> = {
    'wi-fi': 'Wi-Fi',
    'cafe-da-manha': 'Café da manhã',
    'ar-condicionado': 'Ar-condicionado',
    restaurante: 'Restaurante',
    'estacionamento-pontal': 'Estacionamento no Pontal',
    varanda: 'Varanda',
    'cozinha-compartilhada': 'Cozinha compartilhada',
  };
  return map[key] || key;
}

export function PousadaDestaqueSection() {
  const { t } = useTranslation();
  const { generateLink } = useAffiliate();
  const { trackAffiliate } = useAnalytics();
  const thumbs = googleThumbs as Record<string, string>;
  const heroImage = thumbs[pousada.handle] || '/images/hero-bg.jpg';

  return (
    <section id="pousada-destaque" className="py-16 sm:py-20 bg-white">
      <Container>
        <div className="text-center mb-10">
          <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
            ONDE FICAR
          </span>
          <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mt-2">
            HOSPEDAGEM SUGERIDA PELO GUIA
          </h2>
          <p className="font-editorial text-lg text-warm-gray mt-2 max-w-2xl mx-auto">
            Nossa escolha em Encantadas com reserva direta e preço justo
          </p>
        </div>

        {/* Card 50/50 */}
        <div className="rounded-2xl border border-sand-200 overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] bg-white">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Imagem */}
            <div className="relative h-80 md:h-auto md:min-h-[600px]">
              <img
                src={heroImage}
                alt={pousada.title}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                <span className="bg-coral text-white text-xs font-body font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                  {t('entity.partnerBadge', 'Parceira')}
                </span>
                <span className="bg-white/90 backdrop-blur-sm text-warm-dark text-xs font-body font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {pousada.vilarejoName}
                </span>
              </div>

              {/* Distâncias */}
              <div className="absolute bottom-4 left-4 right-4 flex gap-3">
                <span className="bg-white/90 backdrop-blur-sm text-warm-dark text-xs font-body font-semibold px-3 py-1.5 rounded-full">
                  🏖️ {pousada.location.distanceToBeach} da praia
                </span>
                <span className="bg-white/90 backdrop-blur-sm text-warm-dark text-xs font-body font-semibold px-3 py-1.5 rounded-full">
                  ⛵ {pousada.location.distanceToPier} do trapiche
                </span>
              </div>
            </div>

            {/* Conteúdo */}
            <div className="p-6 lg:p-10 flex flex-col justify-center">
              <h3 className="font-display text-3xl text-warm-dark mb-1">
                {pousada.title.toUpperCase()}
              </h3>
              <p className="font-editorial text-lg text-warm-gray mb-4">
                {pousada.subtitle}
              </p>

              {/* Ratings */}
              <div className="flex flex-wrap gap-4 mb-5">
                <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-100 px-3 py-1.5 rounded-full">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <span className="font-body text-sm font-semibold text-amber-700">
                    {pousada.rating.google}
                  </span>
                  <span className="font-body text-xs text-amber-600">
                    Google · {pousada.reviewCount.google} reviews
                  </span>
                </div>
                <div className="flex items-center gap-1.5 bg-teal-50 border border-teal-100 px-3 py-1.5 rounded-full">
                  <Star className="w-4 h-4 text-teal-500 fill-teal-500" />
                  <span className="font-body text-sm font-semibold text-teal-700">
                    {pousada.rating.booking}
                  </span>
                  <span className="font-body text-xs text-teal-600">
                    Booking · {pousada.reviewCount.booking} reviews
                  </span>
                </div>
              </div>

              {/* Descrição */}
              <p className="font-body text-warm-gray leading-relaxed mb-5">
                {pousada.description}
              </p>

              {/* Amenities */}
              <div className="mb-5">
                <h4 className="font-body text-sm font-semibold text-warm-dark mb-2">
                  {t('entity.sectionAmenities', 'Comodidades')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {pousada.amenities.map((a) => (
                    <span
                      key={a}
                      className="font-body text-xs bg-teal-light/10 text-teal-deep border border-teal-light/20 px-3 py-1 rounded-full capitalize"
                    >
                      {amenityLabel(a)}
                    </span>
                  ))}
                </div>
              </div>

              {/* Quartos */}
              <div className="mb-5">
                <h4 className="font-body text-sm font-semibold text-warm-dark mb-2">
                  {t('entity.sectionRoomTypes', 'Tipos de quarto')}
                </h4>
                <div className="space-y-2">
                  {pousada.roomTypes.map((room) => (
                    <div
                      key={room.name}
                      className="bg-white border border-sand-200 rounded-xl p-3 flex justify-between items-center shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                    >
                      <div>
                        <span className="font-body font-semibold text-warm-dark text-sm">
                          {room.name}
                        </span>
                        <p className="font-body text-xs text-warm-gray">{room.description}</p>
                      </div>
                      <span className="font-display text-coral text-lg">
                        R$ {room.price}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="font-body text-xs text-warm-gray mt-1.5">
                  {t('guide.pousadaDestaque.priceNote', 'Diária média: R$ {min} – R$ {max}')
                    .replace('{min}', String(pousada.priceRange.min))
                    .replace('{max}', String(pousada.priceRange.max))}
                </p>
              </div>

              {/* Reviews */}
              <div className="mb-5">
                <h4 className="font-body text-sm font-semibold text-warm-dark mb-2">
                  {t('guide.pousadaDestaque.reviewsTitle', 'O que dizem os hóspedes')}
                </h4>
                <div className="space-y-3">
                  {pousada.reviews.map((review, i) => (
                    <div key={i} className="bg-sand-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Quote className="w-4 h-4 text-coral" />
                        <span className="font-body text-xs font-semibold text-warm-dark">
                          {review.author}
                        </span>
                        <span className="font-body text-xs text-warm-gray">
                          · {review.source === 'booking' ? 'Booking' : 'Google'}
                        </span>
                        <span className="font-body text-xs text-amber-600 font-semibold">
                          ★ {review.rating}
                        </span>
                      </div>
                      <p className="font-body text-sm text-warm-gray italic">
                        "{review.text}"
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vantagens reserva direta */}
              <div className="mb-6">
                <h4 className="font-body text-sm font-semibold text-warm-dark mb-2">
                  {t('guide.pousadaDestaque.advantagesTitle', 'Por que reservar direto?')}
                </h4>
                <ul className="space-y-1.5">
                  {pousada.directBookingAdvantage.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-teal-deep flex-shrink-0 mt-0.5" />
                      <span className="font-body text-sm text-warm-gray">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3">
                <WhatsAppButton
                  provider={pousada.provider}
                  source="guia-pousada-destaque"
                  label={t('guide.pousadaDestaque.whatsappCta', 'Reservar pelo WhatsApp')}
                  variant="button"
                  className="flex-1 justify-center text-sm py-3"
                />
                <a
                  href={generateLink('booking', 'pousada-destaque', { ss: 'Ilha do Mel' })}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  onClick={() => trackAffiliate({ provider: 'Booking.com', platform: 'booking', productType: 'hotel' })}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-white border-2 border-coral text-coral hover:bg-coral hover:text-white font-body font-semibold rounded-xl px-6 py-3 transition-all text-sm"
                >
                  {t('guide.pousadaDestaque.bookingCta', 'Ver no Booking.com')}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              <p className="font-body text-xs text-warm-gray mt-3 text-center">
                {t('guide.affiliateBadge', 'Link afiliado — você não paga mais por isso')}
              </p>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
